import {
  forwardRef,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import AttachFileRounded from "@mui/icons-material/AttachFileRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import SendRounded from "@mui/icons-material/SendRounded";
import {
  Avatar,
  Badge,
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  COMPOSER_ERROR_KEYS,
  type AssistantComposerState,
} from "../model/useAssistantComposer";

export interface AssistantComposerProps {
  composer: AssistantComposerState;
  busy: boolean;
}

/** Выбор фото с диска по кнопке скрепки. */
const PhotoFileInput = forwardRef<
  HTMLInputElement,
  { onFiles: (files: FileList | null) => void }
>(({ onFiles }, ref) => (
  <input
    ref={ref}
    type="file"
    accept="image/jpeg,image/png,image/webp"
    multiple
    hidden
    onChange={(e) => {
      onFiles(e.target.files);
      e.target.value = "";
    }}
  />
));
PhotoFileInput.displayName = "PhotoFileInput";

/**
 * Поле помощника внизу панели: текст до 2000 символов и до 4 фото (кнопка,
 * вставка из буфера, перетаскивание), Enter отправляет, Shift+Enter
 * переносит строку.
 */
export const AssistantComposer = forwardRef<
  HTMLTextAreaElement,
  AssistantComposerProps
>(({ composer, busy }, ref) => {
  const { t } = useTranslation();
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const { text, photos, uploading, error } = composer;
  const canSend =
    !busy && uploading === 0 && (text.trim().length > 0 || photos.length > 0);

  const onKeyDown = (
    e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      composer.submit();
    }
  };

  const hasFiles = (e: DragEvent) =>
    Array.from(e.dataTransfer.types).includes("Files");

  const onDragOver = (e: DragEvent) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setDragOver(true);
  };

  const onDragLeave = (e: DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      setDragOver(false);
    }
  };

  const onDrop = (e: DragEvent) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    setDragOver(false);
    void composer.addFiles(e.dataTransfer.files);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        gap={1}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        sx={({ palette, shape }) => ({
          px: 1.5,
          py: 1,
          boxSizing: "border-box",
          borderRadius: `${shape.borderRadius}px`,
          border: `1px ${dragOver ? "dashed" : "solid"} ${
            error
              ? palette.error.main
              : dragOver
                ? palette.primary.main
                : palette.divider
          }`,
          bgcolor: dragOver ? palette.action.hover : palette.background.paper,
          "&:focus-within": {
            borderColor: error ? palette.error.main : palette.primary.main,
          },
        })}
      >
        {(photos.length > 0 || uploading > 0) && (
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            {photos.map((p) => (
              <Badge
                key={p.id}
                overlap="circular"
                badgeContent={
                  <IconButton
                    size="small"
                    aria-label={t("partnerAssistant.removePhoto")}
                    onClick={() => composer.removePhoto(p.id)}
                    sx={{ p: 0, bgcolor: "background.paper" }}
                  >
                    <CloseRounded sx={{ fontSize: 14 }} />
                  </IconButton>
                }
              >
                <Avatar
                  variant="rounded"
                  src={p.url}
                  sx={{ width: 40, height: 40 }}
                />
              </Badge>
            ))}
            {uploading > 0 && <CircularProgress size={18} />}
          </Stack>
        )}
        <InputBase
          inputRef={ref}
          value={text}
          onChange={(e) => composer.setText(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={(e) => composer.pasteFiles(e)}
          placeholder={t("partnerAssistant.placeholder")}
          multiline
          minRows={2}
          maxRows={10}
          sx={{ width: "100%" }}
          inputProps={{ "aria-label": t("partnerAssistant.placeholder") }}
        />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Tooltip title={t("partnerAssistant.attachPhoto")}>
            <span>
              <IconButton
                size="small"
                edge="start"
                aria-label={t("partnerAssistant.attachPhoto")}
                onClick={() => fileInput.current?.click()}
                disabled={!composer.canAttach}
              >
                <AttachFileRounded fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("partnerAssistant.send")}>
            <span>
              <IconButton
                size="small"
                edge="end"
                color="primary"
                aria-label={t("partnerAssistant.send")}
                onClick={composer.submit}
                disabled={!canSend}
              >
                <SendRounded fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <PhotoFileInput
          ref={fileInput}
          onFiles={(files) => void composer.addFiles(files)}
        />
      </Stack>
      {error && (
        <Box
          role="alert"
          sx={{ color: "error.main", typography: "caption", px: 1.5, pt: 0.5 }}
        >
          {t(COMPOSER_ERROR_KEYS[error])}
        </Box>
      )}
    </Box>
  );
});
AssistantComposer.displayName = "AssistantComposer";
